import { Rotate3d, Boxes, Grid3x3, Droplets, RefreshCcw, Gauge, X } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import ARButton from "@/components/panels/ARButton";
import { useStore } from "@/store/useStore";

function Row({ label, value, children }) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium tracking-wide text-slate-300">{label}</span>
        {value != null && <span className="spec-val text-xs cyan-text">{value}</span>}
      </div>
      {children}
    </div>
  );
}

const SHADES = [
  { id: "shaded", label: "Shaded", icon: Boxes },
  { id: "wireframe", label: "CAD", icon: Grid3x3 },
  { id: "xray", label: "X-Ray", icon: Droplets },
];

export default function ControlDock({ mobile }) {
  const autoRotate = useStore((s) => s.autoRotate);
  const setAutoRotate = useStore((s) => s.setAutoRotate);
  const rotateSpeed = useStore((s) => s.rotateSpeed);
  const setRotateSpeed = useStore((s) => s.setRotateSpeed);
  const explode = useStore((s) => s.explode);
  const setExplode = useStore((s) => s.setExplode);
  const shading = useStore((s) => s.shading);
  const setShading = useStore((s) => s.setShading);
  const opacity = useStore((s) => s.opacity);
  const setOpacity = useStore((s) => s.setOpacity);
  const resetCamera = useStore((s) => s.resetCamera);
  const setMobilePanel = useStore((s) => s.setMobilePanel);

  return (
    <aside
      data-testid="right-control-panel"
      className={
        mobile
          ? "glass rounded-t-3xl p-5 space-y-5"
          : "glass fixed right-5 top-24 z-40 w-[18rem] rounded-2xl p-5 space-y-5"
      }
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gauge className="h-4 w-4 cyan-text" />
          <h3 className="text-sm font-semibold tracking-wide text-white">Spatial Controls</h3>
        </div>
        {mobile && (
          <button onClick={() => setMobilePanel(null)} data-testid="close-controls-mobile">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        )}
      </div>
      <div className="eyebrow violet-text">CAD / Render Engine</div>

      <Row label="360° Auto-Rotate">
        <div className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2.5">
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <Rotate3d className="h-4 w-4 cyan-text" /> Orbit motion
          </div>
          <Switch
            checked={autoRotate}
            onCheckedChange={setAutoRotate}
            data-testid="toggle-auto-rotate"
          />
        </div>
      </Row>

      {autoRotate && (
        <Row label="Rotation Speed" value={`${rotateSpeed.toFixed(1)}x`}>
          <Slider
            value={[rotateSpeed]}
            min={0.5}
            max={2}
            step={0.1}
            onValueChange={([v]) => setRotateSpeed(v)}
            data-testid="slider-rotate-speed"
          />
        </Row>
      )}

      <Row label="Explode Components" value={`${Math.round(explode * 100)}%`}>
        <Slider
          value={[explode]}
          min={0}
          max={1}
          step={0.01}
          onValueChange={([v]) => setExplode(v)}
          data-testid="toggle-exploded-view"
        />
      </Row>

      <Row label="Shading Mode">
        <div className="grid grid-cols-3 gap-2" data-testid="toggle-wireframe-mode">
          {SHADES.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setShading(id)}
              data-testid={`shading-${id}`}
              className={`pill flex flex-col items-center gap-1 rounded-xl border px-2 py-2.5 text-[0.72rem] ${
                shading === id
                  ? "pill-active-cyan border-transparent"
                  : "border-white/10 bg-white/[0.03] text-slate-300"
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      </Row>

      <Row label="Surface Transparency" value={`${Math.round(opacity * 100)}%`}>
        <Slider
          value={[opacity]}
          min={0.1}
          max={1}
          step={0.01}
          onValueChange={([v]) => setOpacity(v)}
          data-testid="slider-transparency"
        />
      </Row>

      <button
        onClick={resetCamera}
        data-testid="button-camera-reset"
        className="pill flex w-full items-center justify-center gap-2 rounded-xl border border-violet-400/30 bg-violet-500/10 py-2.5 text-xs font-medium text-violet-200 hover:bg-violet-500/20"
      >
        <RefreshCcw className="h-4 w-4" /> Reset Camera / WebXR
      </button>

      <ARButton />
    </aside>
  );
}
